import React, { useState, useEffect } from 'react';
import './EnhancedSectionEditor.css';
import { API_URL } from '../config';

interface EnhancedSection {
  id?: number;
  numero: number;
  libelle: string;
  longueur: number;
  largeur: number;
  trafic: string;
  structure: string;
  construction_date?: string;
  surface_glissante: number;
  degradation_revetement: number;
  fissures_longitudinales: number;
  fissures_transversales: number;
  nids_poule: number;
  deformation: number;
  deflection_t1?: number;
  deflection_t2?: number;
  deflection_t3?: number;
  deflection_t4?: number;
  deflection_t5?: number;
  photos?: string;
}

interface EnhancedSectionEditorProps {
  section: EnhancedSection;
  onUpdate: (field: keyof EnhancedSection, value: any) => void;
  onDelete: () => void;
}

const EnhancedSectionEditor: React.FC<EnhancedSectionEditorProps> = ({
  section,
  onUpdate,
  onDelete
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (section.photos) {
      try {
        setPhotos(JSON.parse(section.photos));
      } catch {
        setPhotos([]);
      }
    }
  }, [section.photos]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !section.id) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', e.target.files[0]);
    
    try {
      const response = await fetch(`${API_URL}/sections/${section.id}/photos`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      setPhotos(data.photos);
      onUpdate('photos', JSON.stringify(data.photos));
    } catch (error) {
      console.error('Error uploading photo:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (index: number) => {
    if (!section.id) return;
    
    try {
      const response = await fetch(`${API_URL}/sections/${section.id}/photos/${index}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      setPhotos(data.photos);
      onUpdate('photos', JSON.stringify(data.photos));
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const getConditionColor = (value: number) => {
    if (value === 0) return '#4CAF50';
    if (value <= 2) return '#FF9800';
    if (value <= 4) return '#FF5722';
    return '#F44336';
  };

  const getConditionLabel = (value: number) => {
    if (value === 0) return 'Excellent';
    if (value <= 2) return 'Bon';
    if (value <= 4) return 'Moyen';
    return 'Mauvais';
  };

  return (
    <div className={`enhanced-section-card ${collapsed ? 'collapsed' : ''}`}>
      <div className="section-header" onClick={() => setCollapsed(!collapsed)}>
        <span className="collapse-arrow">{collapsed ? '▶' : '▼'}</span>
        <div className="section-title">
          <h4>🛣️ Tronçon {section.numero} - {section.libelle}</h4>
          <span className="section-surface">
            {(section.longueur * section.largeur).toFixed(0)} m²
          </span>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="delete-btn">
          🗑️
        </button>
      </div>

      {!collapsed && (
        <>
        <div className="section-tabs">
        <button 
          className={`section-tab ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          📋 Général
        </button>
        <button 
          className={`section-tab ${activeTab === 'visual' ? 'active' : ''}`}
          onClick={() => setActiveTab('visual')}
        >
          👁️ Visuel
        </button>
        <button 
          className={`section-tab ${activeTab === 'structural' ? 'active' : ''}`}
          onClick={() => setActiveTab('structural')}
        >
          🔧 Structurel
        </button>
        <button 
          className={`section-tab ${activeTab === 'photos' ? 'active' : ''}`}
          onClick={() => setActiveTab('photos')}
        >
          📷 Photos ({photos.length})
        </button>
      </div>

      <div className="section-content">
        {activeTab === 'basic' && (
          <div className="basic-tab">
            <div className="input-grid">
              <div className="input-group">
                <label>Libellé</label>
                <input
                  type="text"
                  value={section.libelle}
                  onChange={(e) => onUpdate('libelle', e.target.value)}
                />
              </div>

              <div className="input-group">
                <label>Longueur (m)</label>
                <input
                  type="number"
                  value={section.longueur}
                  onChange={(e) => onUpdate('longueur', parseFloat(e.target.value))}
                />
              </div>

              <div className="input-group">
                <label>Largeur (m)</label>
                <input
                  type="number"
                  step="0.1"
                  value={section.largeur}
                  onChange={(e) => onUpdate('largeur', parseFloat(e.target.value))}
                />
              </div>

              <div className="input-group">
                <label>Date de construction</label>
                <input
                  type="date"
                  value={section.construction_date ? section.construction_date.split('T')[0] : ''}
                  onChange={(e) => onUpdate('construction_date', e.target.value || null)}
                />
              </div>

              <div className="input-group">
                <label>Classe de trafic</label>
                <select
                  value={section.trafic}
                  onChange={(e) => onUpdate('trafic', e.target.value)}
                >
                  <option value="T0">T0 - Très faible</option>
                  <option value="T1">T1 - Faible</option>
                  <option value="T2">T2 - Moyen</option>
                  <option value="T3">T3 - Fort</option>
                </select>
              </div>

              <div className="input-group">
                <label>Type de structure</label>
                <select
                  value={section.structure}
                  onChange={(e) => onUpdate('structure', e.target.value)}
                >
                  <option value="E">Enrobé (E)</option>
                  <option value="B">Béton (B)</option>
                  <option value="S">Souple (S)</option>
                  <option value="M">Mixte (M)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'visual' && (
          <div className="visual-tab">
            <div className="visual-header">
              <h5>🔍 Indicateurs Visuels</h5>
              <div className="severity-legend">
                <span className="legend-item excellent">0-1 Excellent</span>
                <span className="legend-item good">2 Bon</span>
                <span className="legend-item average">3 Moyen</span>
                <span className="legend-item poor">4-5 Mauvais</span>
              </div>
            </div>
            
            <div className="indicators-grid">
              {[
                { key: 'surface_glissante', label: 'Surface glissante', icon: '🧊', desc: 'Adhérence de la chaussée' },
                { key: 'degradation_revetement', label: 'Dégradation revêtement', icon: '🛣️', desc: 'État général du revêtement' },
                { key: 'fissures_longitudinales', label: 'Fissures longitudinales', icon: '↕️', desc: 'Fissures dans le sens de circulation' },
                { key: 'fissures_transversales', label: 'Fissures transversales', icon: '↔️', desc: 'Fissures perpendiculaires' },
                { key: 'nids_poule', label: 'Nids de poule', icon: '🕳️', desc: 'Trous et cavités dans la chaussée' },
                { key: 'deformation', label: 'Déformation', icon: '〰️', desc: 'Affaissement et ondulations' }
              ].map(({ key, label, icon, desc }) => {
                const value = section[key as keyof EnhancedSection] as number || 0;
                return (
                  <div key={key} className="indicator-card">
                    <div className="indicator-header">
                      <div className="indicator-title">
                        <span className="indicator-icon">{icon}</span>
                        <div className="indicator-text">
                          <h6>{label}</h6>
                          <p>{desc}</p>
                        </div>
                      </div>
                      <div className="indicator-badge">
                        <span className={`severity-badge severity-${value <= 1 ? 'excellent' : value === 2 ? 'good' : value === 3 ? 'average' : 'poor'}`}>
                          {value <= 1 ? 'Excellent' : value === 2 ? 'Bon' : value === 3 ? 'Moyen' : 'Mauvais'}
                        </span>
                        <span className="severity-value">{value}</span>
                      </div>
                    </div>
                    
                    <div className="indicator-controls">
                      <div className="severity-buttons">
                        {[0, 1, 2, 3, 4, 5].map(level => (
                          <button
                            key={level}
                            className={`severity-btn ${value === level ? 'active' : ''} severity-${level <= 1 ? 'excellent' : level === 2 ? 'good' : level === 3 ? 'average' : 'poor'}`}
                            onClick={() => onUpdate(key as keyof EnhancedSection, level)}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="photos-tab">
            <h5>📷 Documentation Photographique</h5>
            
            <div className="photo-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                disabled={uploading}
                id={`photo-upload-${section.id}`}
                style={{ display: 'none' }}
              />
              <label htmlFor={`photo-upload-${section.id}`} className="upload-btn">
                {uploading ? '⏳ Upload...' : '📤 Ajouter Photo'}
              </label>
            </div>

            <div className="photos-grid">
              {photos.map((photo, index) => (
                <div key={index} className="photo-item">
                  <img src={`${API_URL}/${photo}`} alt={`Photo ${index + 1}`} />
                  <button 
                    className="delete-photo-btn"
                    onClick={() => handleDeletePhoto(index)}
                  >
                    🗑️
                  </button>
                </div>
              ))}
              {photos.length === 0 && (
                <div className="no-photos">
                  <p>📷 Aucune photo</p>
                  <p>Ajoutez des photos pour documenter l'état de ce tronçon</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'structural' && (
          <div className="structural-tab">
            <h5>📏 Mesures de Déflexion (mm/100)</h5>
            <div className="deflection-grid">
              {[
                { key: 'deflection_t1', label: 'T1 (0m)' },
                { key: 'deflection_t2', label: 'T2 (20cm)' },
                { key: 'deflection_t3', label: 'T3 (30cm)' },
                { key: 'deflection_t4', label: 'T4 (45cm)' },
                { key: 'deflection_t5', label: 'T5 (60cm)' }
              ].map(({ key, label }) => (
                <div key={key} className="deflection-item">
                  <label>{label}</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Non mesuré"
                    value={section[key as keyof EnhancedSection] || ''}
                    onChange={(e) => onUpdate(key as keyof EnhancedSection, e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </div>
              ))}
            </div>
            
            <div className="deflection-info">
              <h6>ℹ️ Interprétation des mesures</h6>
              <div className="info-grid">
                <div className="info-item good">
                  <span className="info-range">≤ 50</span>
                  <span className="info-label">Excellent</span>
                </div>
                <div className="info-item ok">
                  <span className="info-range">51-100</span>
                  <span className="info-label">Bon</span>
                </div>
                <div className="info-item warning">
                  <span className="info-range">101-200</span>
                  <span className="info-label">Moyen</span>
                </div>
                <div className="info-item bad">
                  <span className="info-range">201-400</span>
                  <span className="info-label">Mauvais</span>
                </div>
                <div className="info-item critical">
                  <span className="info-range">&gt; 400</span>
                  <span className="info-label">Critique</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default EnhancedSectionEditor;